import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { safeStorage } from '@/utils/safeStorage';

const STORAGE_KEYS = {
  DISMISSED_AT: 'newsletter_popup_dismissed_at',
  VISITS_SINCE: 'newsletter_popup_visits_since',
  SUBSCRIBED: 'mw_newsletter_subscribed',
};

const VISITS_BEFORE_RESHOWING = 5;
const POPUP_DELAY_MS = 3000;

/** Signup for this offer lives on this route, including the account form. */
const NEWSLETTER_POPUP_EXCLUDED_PATHS = ['/workshops/list-your-workshop'];

export function isNewsletterPopupExcluded(pathname?: string): boolean {
  if (!pathname) return false;
  const normalized = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return NEWSLETTER_POPUP_EXCLUDED_PATHS.some(
    (excluded) => normalized === excluded || normalized.startsWith(`${excluded}/`),
  );
}

export const useNewsletterPopup = (authModalOpen?: boolean, pathname?: string) => {
  const [showPopup, setShowPopup] = useState(false);
  const { user, isSubscribed } = useAuth();

  // Hide popup when auth modal opens
  useEffect(() => {
    if (authModalOpen && showPopup) {
      setShowPopup(false);
    }
  }, [authModalOpen, showPopup]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isNewsletterPopupExcluded(pathname)) {
      setShowPopup(false);
      return;
    }
    // Don't show for authenticated users, premium subscribers, or when auth modal is open
    if (user || isSubscribed || authModalOpen) {
      return;
    }

    // Check if already subscribed via newsletter
    const isNewsletterSubscribed = safeStorage.getItem(STORAGE_KEYS.SUBSCRIBED) === 'true';
    if (isNewsletterSubscribed) {
      return;
    }

    const dismissedAt = safeStorage.getItem(STORAGE_KEYS.DISMISSED_AT);
    const visitsSince = parseInt(safeStorage.getItem(STORAGE_KEYS.VISITS_SINCE) || '0', 10);

    if (dismissedAt) {
      // User has dismissed before - check visit count
      if (visitsSince < VISITS_BEFORE_RESHOWING) {
        // Increment visit count and don't show
        safeStorage.setItem(STORAGE_KEYS.VISITS_SINCE, String(visitsSince + 1));
        return;
      } else {
        // Reset for next cycle - will show popup
        safeStorage.removeItem(STORAGE_KEYS.DISMISSED_AT);
        safeStorage.removeItem(STORAGE_KEYS.VISITS_SINCE);
      }
    }

    // Show popup after delay
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, POPUP_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user, isSubscribed, pathname]);

  const dismissPopup = () => {
    setShowPopup(false);
    safeStorage.setItem(STORAGE_KEYS.DISMISSED_AT, Date.now().toString());
    safeStorage.setItem(STORAGE_KEYS.VISITS_SINCE, '0');
  };

  const subscribeSuccess = () => {
    setShowPopup(false);
    safeStorage.setItem(STORAGE_KEYS.SUBSCRIBED, 'true');
  };

  return {
    showPopup,
    dismissPopup,
    subscribeSuccess,
  };
};
